import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

const TOUCH_ACTIVATOR_SELECTOR = 'a[href], button:not([disabled]), summary';
const TOUCH_MOVE_TOLERANCE = 10;
const TOUCH_COMPATIBILITY_CLICK_WINDOW = 800;
const MENU_ITEMS_ENTERING_CLASS = 'menu-items-entering';
const TOUCH_COMPATIBILITY_CLICK_UNTIL = Symbol.for(
  'nailreformation.headerDrawer.touchCompatibilityClickUntil'
);
const TOUCH_COMPATIBILITY_CLICK_LISTENER = Symbol.for(
  'nailreformation.headerDrawer.touchCompatibilityClickListener'
);

/**
 * Consume the native compatibility click that may follow a touch activation.
 * This is also called by HeaderDrawer's delegated actions because Shopify's
 * document-level action router runs before later capture listeners.
 * @param {Event} event
 * @returns {boolean}
 */
function consumeTouchCompatibilityClick(event) {
  if (event.type !== 'click' || !(event.target instanceof Element)) return false;

  const activator = event.target.closest(TOUCH_ACTIVATOR_SELECTOR);
  if (!(activator instanceof HTMLElement)) return false;

  const touchClick = activator[TOUCH_COMPATIBILITY_CLICK_UNTIL];
  if (!touchClick || touchClick.dispatching) return false;

  delete activator[TOUCH_COMPATIBILITY_CLICK_UNTIL];
  if (event.detail === 0 || performance.now() > touchClick.until) return false;

  event.preventDefault();
  event.stopImmediatePropagation();
  return true;
}

/** @param {MouseEvent} event */
function suppressTouchCompatibilityClick(event) {
  consumeTouchCompatibilityClick(event);
}

if (!document[TOUCH_COMPATIBILITY_CLICK_LISTENER]) {
  document.addEventListener('click', suppressTouchCompatibilityClick, true);
  document[TOUCH_COMPATIBILITY_CLICK_LISTENER] = true;
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #mainTransitionVersion = 0;
  #submenuTransitionVersions = new WeakMap();
  #touchPointerId;
  #touchActivator;
  #touchStartX = 0;
  #touchStartY = 0;
  #touchMoved = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('pointerdown', this.#onTouchPointerDown);
    this.addEventListener('pointermove', this.#onTouchPointerMove);
    this.addEventListener('pointerup', this.#onTouchPointerUp);
    this.addEventListener('pointercancel', this.#onTouchPointerCancel);
    this.#setupAnimatedElementListeners();

    // The native <details> element can be opened before this module finishes loading.
    // Reconcile that progressively-enhanced state instead of making the first tap a no-op.
    if (this.refs.details.classList.contains('menu-closing')) {
      this.refs.menuDrawer.inert = true;
      this.#scheduleMainCloseReset(++this.#mainTransitionVersion);
    } else if (this.refs.details.open) {
      this.refs.details.setAttribute('data-native-open', '');
      this.refs.details.classList.add('menu-open');
      this.refs.details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
      this.refs.menuDrawer.inert = false;
      this.#scheduleMainOpenFocus(++this.#mainTransitionVersion);
    } else {
      this.refs.menuDrawer.inert = true;
    }
  }

  disconnectedCallback() {
    this.#mainTransitionVersion += 1;
    if (this.refs.details.open) removeTrapFocus();
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('pointerdown', this.#onTouchPointerDown);
    this.removeEventListener('pointermove', this.#onTouchPointerMove);
    this.removeEventListener('pointerup', this.#onTouchPointerUp);
    this.removeEventListener('pointercancel', this.#onTouchPointerCancel);
    this.#resetTouchPointer();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Return the native control represented by a touch target. Keeping this
   * delegated means links, buttons and disclosures retain their existing
   * click behavior without per-item touch handlers.
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #getTouchActivator(target) {
    if (!(target instanceof Element)) return null;
    if (target.closest('input, select, textarea, [contenteditable="true"]')) return null;

    const activator = target.closest(TOUCH_ACTIVATOR_SELECTOR);
    return activator instanceof HTMLElement && this.contains(activator) ? activator : null;
  }

  /** @param {PointerEvent} event */
  #onTouchPointerDown = (event) => {
    if (event.pointerType !== 'touch' || !event.isPrimary || event.button !== 0) return;

    const activator = this.#getTouchActivator(event.target);
    if (!activator) return;

    delete activator[TOUCH_COMPATIBILITY_CLICK_UNTIL];
    this.#touchPointerId = event.pointerId;
    this.#touchActivator = activator;
    this.#touchStartX = event.clientX;
    this.#touchStartY = event.clientY;
    this.#touchMoved = false;
  };

  /** @param {PointerEvent} event */
  #onTouchPointerMove = (event) => {
    if (event.pointerId !== this.#touchPointerId || this.#touchMoved) return;

    this.#touchMoved =
      Math.hypot(event.clientX - this.#touchStartX, event.clientY - this.#touchStartY) >
      TOUCH_MOVE_TOLERANCE;
  };

  /**
   * iOS Safari may omit its compatibility click when an entry or accordion
   * animation changes rendered content after touchend. Activate the original
   * native control once at pointerup, then ignore only Safari's duplicate click.
   * @param {PointerEvent} event
   */
  #onTouchPointerUp = (event) => {
    if (event.pointerId !== this.#touchPointerId) return;

    const activator = this.#touchActivator;
    const isTap =
      !this.#touchMoved && activator && this.#getTouchActivator(event.target) === activator;
    this.#resetTouchPointer();
    if (!isTap) return;

    const touchClick = {
      dispatching: true,
      until: performance.now() + TOUCH_COMPATIBILITY_CLICK_WINDOW,
    };
    activator[TOUCH_COMPATIBILITY_CLICK_UNTIL] = touchClick;

    try {
      activator.click();
    } finally {
      touchClick.dispatching = false;
    }
  };

  #onTouchPointerCancel = () => {
    this.#resetTouchPointer();
  };

  #resetTouchPointer() {
    this.#touchPointerId = undefined;
    this.#touchActivator = undefined;
    this.#touchMoved = false;
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.open && !this.refs.details.classList.contains('menu-closing');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Start a new transition generation for one submenu.
   * @param {HTMLDetailsElement} details
   * @returns {number}
   */
  #beginSubmenuTransition(details) {
    const transitionVersion = (this.#submenuTransitionVersions.get(details) ?? 0) + 1;
    this.#submenuTransitionVersions.set(details, transitionVersion);
    return transitionVersion;
  }

  /**
   * Check that an async submenu task still belongs to the latest main- and
   * submenu-level user intent.
   * @param {HTMLDetailsElement} details
   * @param {number} submenuTransitionVersion
   * @param {number} mainTransitionVersion
   * @returns {boolean}
   */
  #isCurrentSubmenuTransition(details, submenuTransitionVersion, mainTransitionVersion) {
    return (
      this.#submenuTransitionVersions.get(details) === submenuTransitionVersion &&
      this.#mainTransitionVersion === mainTransitionVersion &&
      this.contains(details)
    );
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (event && consumeTouchCompatibilityClick(event)) return;

    event?.preventDefault();
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    if (event && consumeTouchCompatibilityClick(event)) return;

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.#openMain(summary);
      return;
    }

    const mainTransitionVersion = this.#mainTransitionVersion;
    const submenuTransitionVersion = this.#beginSubmenuTransition(details);

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (
        !this.#isCurrentSubmenuTransition(
          details,
          submenuTransitionVersion,
          mainTransitionVersion
        ) ||
        !this.isOpen
      ) {
        return;
      }

      details.classList.remove('menu-closing');
      details.open = true;
      details.classList.add('menu-open');
      summary.setAttribute('aria-expanded', 'true');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(
        drawer || details,
        () => {
          if (
            this.#isCurrentSubmenuTransition(
              details,
              submenuTransitionVersion,
              mainTransitionVersion
            ) &&
            this.isOpen &&
            details.open &&
            details.classList.contains('menu-open')
          ) {
            trapFocus(details);
          }
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    if (event && consumeTouchCompatibilityClick(event)) return;

    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close(event) {
    if (event && consumeTouchCompatibilityClick(event)) return;

    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.#closeMain(summary);
      return;
    }

    const mainTransitionVersion = this.#mainTransitionVersion;
    const submenuTransitionVersion = this.#beginSubmenuTransition(details);

    details.classList.remove('menu-open');
    details.classList.add('menu-closing');
    summary.setAttribute('aria-expanded', 'false');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (
          !this.#isCurrentSubmenuTransition(
            details,
            submenuTransitionVersion,
            mainTransitionVersion
          ) ||
          details.classList.contains('menu-open')
        ) {
          return;
        }

        reset(details);
        if (this.isOpen) {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Open the main drawer, including reversing an in-progress close transition.
   * @param {HTMLElement} summary
   */
  #openMain(summary) {
    if (this.isOpen) return;

    const details = this.refs.details;
    const wasClosed = !details.open;
    const transitionVersion = ++this.#mainTransitionVersion;

    details.removeAttribute('data-native-open');
    details.classList.remove('menu-closing');
    details.open = true;
    this.#restartMenuItemAnimations();
    details.classList.add('menu-open');
    summary.setAttribute('aria-expanded', 'true');
    this.refs.menuDrawer.inert = false;

    if (wasClosed) this.preventInitialAccordionAnimations(details);
    this.#scheduleMainOpenFocus(transitionVersion);
  }

  /**
   * Start the main drawer's visual close without removing native `open` early.
   * @param {HTMLElement} summary
   */
  #closeMain(summary) {
    if (!this.isOpen) return;

    const details = this.refs.details;
    const transitionVersion = ++this.#mainTransitionVersion;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove(MENU_ITEMS_ENTERING_CLASS);
    details.classList.remove('menu-open');
    details.classList.add('menu-closing');
    this.refs.menuDrawer.inert = true;
    removeTrapFocus();

    this.#scheduleMainCloseReset(transitionVersion);
  }

  /**
   * Give each drawer opening a fresh CSS animation lifecycle. Android Chrome
   * can retain the completed animation when it is driven only by the native
   * details `open` state, so reset and re-apply an explicit entry state.
   */
  #restartMenuItemAnimations() {
    const details = this.refs.details;

    details.classList.remove(MENU_ITEMS_ENTERING_CLASS);
    void details.offsetWidth;
    details.classList.add(MENU_ITEMS_ENTERING_CLASS);
  }

  /**
   * Trap focus only after the latest main-drawer open transition settles.
   * @param {number} transitionVersion
   */
  #scheduleMainOpenFocus(transitionVersion) {
    requestAnimationFrame(() => {
      if (transitionVersion !== this.#mainTransitionVersion || !this.isOpen) return;

      onAnimationEnd(
        this.refs.menuDrawer,
        () => {
          if (transitionVersion === this.#mainTransitionVersion && this.isOpen) {
            trapFocus(this.refs.details);
          }
        },
        { subtree: false }
      );
    });
  }

  /**
   * Remove native `open` only after the latest main-drawer close transition settles.
   * @param {number} transitionVersion
   */
  #scheduleMainCloseReset(transitionVersion) {
    requestAnimationFrame(() => {
      if (
        transitionVersion !== this.#mainTransitionVersion ||
        !this.refs.details.classList.contains('menu-closing')
      ) {
        return;
      }

      onAnimationEnd(
        this.refs.menuDrawer,
        () => {
          if (
            transitionVersion === this.#mainTransitionVersion &&
            this.refs.details.classList.contains('menu-closing')
          ) {
            const openSubmenus = this.refs.details.querySelectorAll(
              'details[open]:not(accordion-custom > details)'
            );
            openSubmenus.forEach(reset);
            this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
            reset(this.refs.details);
          }
        },
        { subtree: false }
      );
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open', 'menu-closing', MENU_ITEMS_ENTERING_CLASS);
  element.removeAttribute('data-native-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');

  const submenu = element.querySelector(
    ':scope > .menu-drawer__submenu, :scope > .sleek-menu-drawer__mega-l2-content'
  );
  if (submenu instanceof HTMLElement) {
    submenu.getAnimations().forEach((animation) => animation.cancel());
    submenu.style.removeProperty('height');
  }
}
